// Global Instructions Rule Applied!

import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { Currency } from './interfaces.ts'
import { RootState } from '../../state_manager/store.ts'

interface CurrencyOption {
  label: string
  value: string
  symbol: string | null | undefined
  code: string | null | undefined
}

interface UseCurrenciesReturn {
  currencies: Currency[]
  getCurrencyById: (id: string | null | undefined) => Currency | null
  formatAmountById: (
    amount: number | string | null | undefined,
    currencyId: string | null | undefined,
    symbol?: string
  ) => string
  getCurrencyOptions: () => CurrencyOption[]
}

export const useCurrencies = (): UseCurrenciesReturn => {
  const currencies = useSelector((state: RootState) => state.globalConfigs.currencies || []) as Currency[]

  const getCurrencyById = useCallback(
    (id: string | null | undefined): Currency | null => {
      return currencies.find((currency: Currency) => currency.id === id) || null
    },
    [currencies]
  )

  const formatAmountById = useCallback(
    (amount: number | string | null | undefined, currencyId: string | null | undefined, symbol?: string): string => {
      let currency: Currency | { symbol: string | null | undefined } | null = null

      if (!amount && amount !== 0) return ''

      if (!currencies || currencies.length === 0) {
        const numAmount = parseFloat(String(amount))
        if (isNaN(numAmount)) return String(amount)
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(numAmount)
      }

      if (!symbol) {
        currency = getCurrencyById(currencyId)
      } else {
        currency = {
          symbol
        }
      }

      const numAmount = parseFloat(String(amount))
      if (isNaN(numAmount)) return String(amount)

      const formattedAmount = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(numAmount)

      return currency ? `${currency.symbol}${formattedAmount}` : formattedAmount
    },
    [getCurrencyById, currencies]
  )

  const getCurrencyOptions = useCallback((): CurrencyOption[] => {
    return currencies.map((currency) => ({
      label: `${currency.code} - ${currency.name}`,
      value: currency.id,
      symbol: currency.symbol,
      code: currency.code
    }))
  }, [currencies])

  return {
    currencies,
    getCurrencyById,
    formatAmountById,
    getCurrencyOptions
  }
}
